import React, {useContext, useEffect, useState} from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext, THEME_MAP } from '../../contexts/ThemeContext.js';
import { ResponsiveContext } from '../../contexts/ResponsiveContext.js';
import './navbar.scss';

const pages = ["home", "server", "infos", "kalender", "streamer", "kontakt"]

const Navbar = () => {
	const [menuIsOpen, setMenuIsOpen] = useState(false);
	const [colorPaletteOpen, setColorPaletteOpen] = useState(false);
	const { theme, setTheme, setIsThemeMenuOpen } = useContext(ThemeContext)
	const { phoneView } = useContext(ResponsiveContext);
	const themeKeys = React.useMemo(() => Object.keys(THEME_MAP), []);

	const handleMenu = () => {
		setIsThemeMenuOpen(prev => !prev)
	}

	const handleTheme = (e, value) => {
		if (value !== theme) {
			setTheme(value);
			localStorage.setItem("theme", value);
		}
	};

	const handleCloseMenu = (e) => {
		e.stopPropagation();
		setMenuIsOpen((prev) => !prev);
	}

	const toggleColorPalette = (e) => {
		e.stopPropagation();
		setColorPaletteOpen((prev) => !prev)
	}

	const useAutoCloseMenuOnScroll = (menuIsOpen, setMenuIsOpen, colorPaletteOpen, setColorPaletteOpen, phoneView) => {
		useEffect(() => {
			const handleScroll = () => {
				if (menuIsOpen && phoneView) {
					setMenuIsOpen(false);
				}
				if (colorPaletteOpen && phoneView) {
					setColorPaletteOpen(false);
				}
			};
	
			if (menuIsOpen && phoneView) {
				window.addEventListener("scroll", handleScroll);
			}
			if (colorPaletteOpen && phoneView) {
				window.addEventListener("scroll", handleScroll);
			}

			return () => {
				window.removeEventListener("scroll", handleScroll);
			};
		}, [menuIsOpen, phoneView, setMenuIsOpen, colorPaletteOpen, setColorPaletteOpen]);
	};

	useAutoCloseMenuOnScroll(menuIsOpen, setMenuIsOpen, colorPaletteOpen, setColorPaletteOpen, phoneView);

	return (
		<nav>
			<div className='nav-wrapper'>
				<div className='logo-wrapper'>
					<NavLink className={'navlink-home'} to={'/'} title='NEXARK - Dein deutsches Ark: Survival Ascended Cluster'>nexark</NavLink>
				</div>
				{!phoneView &&
					<div className='nav-link-wrapper'>
						{pages.map((page, index) => (
							<NavLink
								key={'navlink'+index}
								className={'navlink'}
								to={page === 'home' ? '/' : `/${page}`}
							>
								{page}
							</NavLink>
						))}
						<div className='theme-picker-wrapper' onClick={handleMenu}>
							<p className='theme-picker'>theme</p>
						</div>
					</div>
				}
				{phoneView && <div className='nav-color-palette'>
					<i className={colorPaletteOpen ? 'bi bi-palette' : 'bi bi-palette-fill'} onClick={((e) => {toggleColorPalette(e); setMenuIsOpen(false)})}></i>
				</div>}
				{phoneView && <div className='nav-link-menu' onClick={((e) => {handleCloseMenu(e); setColorPaletteOpen(false)})}>
					<motion.svg
						xmlns="http://www.w3.org/2000/svg"
						width="40"
						height="40"
						viewBox="0 0 24 24"
						fill="none"
						// stroke={theme === "dark" || theme === "light" ? 'var(--blackwhite)' : 'var(--mainBackground)'}
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<motion.line
							x1="3"
							y1="6"
							x2="21"
							y2="6"
							animate={menuIsOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
							transition={{ duration: 0.4, ease: "easeInOut" }}
						/>
						<motion.line
							x1="3"
							y1="12"
							x2="21"
							y2="12"
							animate={menuIsOpen ? { scaleX: 0 } : { scaleX: 1 }}
							style={{ originX: 0.5 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
						/>
						<motion.line
							x1="3"
							y1="18"
							x2="21"
							y2="18"
							animate={menuIsOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
							transition={{ duration: 0.4, ease: "easeInOut" }}
						/>
					</motion.svg>
				</div>}
				{phoneView && (
					<AnimatePresence mode="wait">
						{menuIsOpen && (
							<motion.div
								className="nav-link-items"
								initial={{ opacity: 0, y: -0 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -0 }}
								transition={{
									opacity: { duration: 0.5, ease: "easeInOut" },
									y: { duration: 0.3, ease: "easeOut" }
								}}
								onClick={(e) => handleCloseMenu(e)}
							>
							{pages.map((page, index) => (
								<motion.div
									key={"nav-link-item" + index}
									custom={index}
									initial={{ opacity: 0, y: -20 }}
									animate={{ opacity: 1, y: 0 }}
									exit="exit"
									variants={{
										exit: (i) => ({
											opacity: 0,
											y: -20,
											transition: {
												delay: (pages.length - i - 1) * 0.1,
												duration: 0.3,
												ease: "easeInOut",
											},
										}),
									}}
									transition={{
										delay: menuIsOpen ? index * 0.1 : 0,
										duration: 0.2,
										ease: "easeOut",
									}}
								>
									<NavLink
										className="nav-link-item"
										to={page === "home" ? "/" : `/${page}`}
										onClick={((e) => {handleCloseMenu(e)})}
									>
										{page}
									</NavLink>
								</motion.div>
							))}
							</motion.div>
						)}
						{colorPaletteOpen && (
							<motion.div
								className='nav-link-items'
								onClick={((e) => toggleColorPalette(e))}
							>
								<motion.div
									className='nav-link-item-color-palette'
									key={"nav-link-item-color-palette"}
									initial={{ opacity: 0, y: -20 }}
									animate={{ opacity: 1, y: 0 }}
									exit="exit"
									variants={{
										exit: (i) => ({
											opacity: 0,
											y: -20,
											transition: {
												delay: 0.1,
												duration: 0.3,
												ease: "easeInOut",
											},
										}),
									}}
									transition={{
										delay: colorPaletteOpen ? 0.1 : 0,
										duration: 0.2,
										ease: "easeOut",
									}}
								>
									<p>Standard Themes:</p>
									<div className="nav-theme-picker">
										{themeKeys
											.slice(25,50)
											.map((key) => {
											const { bg, name, color } = THEME_MAP[key];

											return (
												<div
													key={key}
													className={`nav-theme-circle ${theme === key ? "active" : ""}`}
													style={{
														background: bg,
														border: `2px solid ${color}`,
													}}
													title={
														theme === key ? `${name} (aktiv)` : `${name} wählen`
													}
													onClick={(e) => handleTheme(e, key)}
												/>
											);
										})}
									</div>
									<hr />
									<p>Special Themes:</p>
									<div className="nav-theme-picker">
										{themeKeys
											.slice(0,25)
											.map((key) => {
											const { bg, name, color } = THEME_MAP[key];

											return (
												<div
													key={key}
													className={`nav-theme-circle ${theme === key ? "active" : ""}`}
													style={{
														background: bg,
														border: `2px solid ${color}`,
													}}
													title={
														theme === key ? `${name} (aktiv)` : `${name} wählen`
													}
													onClick={(e) => handleTheme(e, key)}
												/>
											);
										})}
									</div>
								</motion.div>
							</motion.div>
						)}
					</AnimatePresence>
				)}
			</div>
		</nav>
	)
}

export default Navbar